import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaCoins, FaDollarSign, FaCalendar, FaStickyNote } from 'react-icons/fa';
import { showSuccess, showError } from '../utils/toast';

const getIstanbulToday = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Istanbul'
}).format(new Date());

const AddAsset = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    type: 'USD',
    amount: '',
    buyPrice: '',
    buyDate: getIstanbulToday(),
    note: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Döviz kategorisi
  const currencyTypes = [
    { value: 'USD', label: 'Amerikan Doları (USD)', category: 'currency' },
    { value: 'EUR', label: 'Euro (EUR)', category: 'currency' },
    { value: 'GBP', label: 'İngiliz Sterlini (GBP)', category: 'currency' },
    { value: 'CHF', label: 'İsviçre Frangı (CHF)', category: 'currency' },
    { value: 'CAD', label: 'Kanada Doları (CAD)', category: 'currency' },
    { value: 'RUB', label: 'Rus Rublesi (RUB)', category: 'currency' },
    { value: 'AED', label: 'BAE Dirhemi (AED)', category: 'currency' },
    { value: 'AUD', label: 'Avustralya Doları (AUD)', category: 'currency' },
    { value: 'DKK', label: 'Danimarka Kronu (DKK)', category: 'currency' },
    { value: 'SEK', label: 'İsveç Kronu (SEK)', category: 'currency' },
    { value: 'NOK', label: 'Norveç Kronu (NOK)', category: 'currency' },
    { value: 'JPY', label: 'Japon Yeni (JPY)', category: 'currency' },
    { value: 'KWD', label: 'Kuveyt Dinarı (KWD)', category: 'currency' },
    { value: 'SAR', label: 'Suudi Arabistan Riyali (SAR)', category: 'currency' },
    { value: 'QAR', label: 'Katar Riyali (QAR)', category: 'currency' },
    { value: 'CNY', label: 'Çin Yuanı (CNY)', category: 'currency' },
    { value: 'PLN', label: 'Polonya Zlotisi (PLN)', category: 'currency' },
    { value: 'BGN', label: 'Bulgar Levası (BGN)', category: 'currency' },
    { value: 'AZN', label: 'Azerbaycan Manatı (AZN)', category: 'currency' },
  ];

  const goldTypes = [
    { value: 'GRAM', label: 'Gram Altın', category: 'gold' },
    { value: 'GRAM_HAS', label: 'Has Altın', category: 'gold' },
    { value: 'CEYREK', label: 'Çeyrek Altın', category: 'gold' },
    { value: 'YARIM', label: 'Yarım Altın', category: 'gold' },
    { value: 'TAM', label: 'Tam Altın', category: 'gold' },
    { value: 'XAU_CUM', label: 'Cumhuriyet Altını', category: 'gold' },
    { value: 'ATA', label: 'Ata Altın', category: 'gold' },
    { value: 'AYAR_14', label: '14 Ayar Altın', category: 'gold' },
    { value: 'AYAR_18', label: '18 Ayar Altın', category: 'gold' },
    { value: 'BILEZIK_22', label: '22 Ayar Bilezik', category: 'gold' },
    { value: 'GREMSE', label: 'Gremse Altın', category: 'gold' },
  ];

  // Gümüş kategorisi
  const silverTypes = [
    { value: 'XAGTRYG', label: 'Gram Gümüş', category: 'silver' },
    { value: 'PLATIN', label: 'Gram Platin', category: 'silver' },
    { value: 'PALADYUM', label: 'Gram Paladyum', category: 'silver' },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        type: formData.type,
        amount: parseFloat(formData.amount),
        buyDate: formData.buyDate,
        note: formData.note
      };

      // Eğer alış fiyatı girilmişse ekle
      if (formData.buyPrice && parseFloat(formData.buyPrice) > 0) {
        payload.buyPrice = parseFloat(formData.buyPrice);
      }

      await axios.post('/api/portfolio', payload);
      showSuccess('Varlık başarıyla eklendi!');
      navigate('/portfolio');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Bir hata oluştu';
      setError(errorMsg);
      showError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-5 sm:py-8 transition-colors duration-300 ease-in-out overflow-x-hidden">
      <div className="max-w-3xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-8 border border-gray-200 dark:border-gray-700 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8">Yeni Varlık Ekle</h1>

          {error && (
            <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <FaCoins className="text-primary-600 dark:text-primary-400" />
                  <span>Varlık Türü</span>
                </div>
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="mt-1 block w-full pl-3 pr-10 py-3 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md"
              >
                <optgroup label="Döviz">
                  {currencyTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Altın">
                  {goldTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Gümüş">
                  {silverTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Miktar
                </label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  step="0.01"
                  min="0.01"
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label htmlFor="buyPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FaDollarSign className="text-primary-600 dark:text-primary-400" />
                      <span>Alış Fiyatı (₺)</span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Opsiyonel</span>
                  </div>
                </label>
                <input
                  type="number"
                  id="buyPrice"
                  name="buyPrice"
                  value={formData.buyPrice}
                  onChange={handleChange}
                  step="0.01"
                  min="0.01"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Boş bırakırsanız tarih bazlı hesaplanır"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Boş bırakırsanız, seçtiğiniz tarihteki fiyat otomatik hesaplanır
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="buyDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <FaCalendar className="text-primary-600 dark:text-primary-400" />
                  <span>Alış Tarihi</span>
                </div>
              </label>
              <input
                type="date"
                id="buyDate"
                name="buyDate"
                value={formData.buyDate}
                onChange={handleChange}
                required
                max={getIstanbulToday()}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label htmlFor="note" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <FaStickyNote className="text-primary-600 dark:text-primary-400" />
                  <span>Not (Opsiyonel)</span>
                </div>
              </label>
              <textarea
                id="note"
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows="3"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Ek notlar ekleyebilirsiniz..."
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary-600 dark:bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 dark:hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Ekleniyor...' : 'Varlık Ekle'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/portfolio')}
                className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                İptal
              </button>
            </div>
          </form>

          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-400 mb-2">İpucu</h3>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
              <li>Alış fiyatını boş bırakırsanız, sistem <strong>alış tarihine göre</strong> otomatik hesaplar</li>
              <li>Sadece alış tarihi ve miktarı girmek yeterli!</li>
              <li>Sistem otomatik olarak güncel fiyatlarla karşılaştırarak kar/zarar durumunuzu hesaplar</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAsset;
